// apps/web/src/shared/corePathPosition.ts
// ─────────────────────────────────────────────────────────────────────────
// Core Path completion model ("open doors", Module Levels V2).
//
// The Core Path is NEVER gated: every lesson in every Core Module stays open
// forever. The final test is not a gate and not a key. It is what completion
// MEANS on the Core Path: a module counts as complete when its final test is
// passed. Two derived facts fall out of the existing records:
//
//   finalsPassed  = how many modules count as complete
//   next up       = the first module (in path order) whose final test
//                   isn't passed. A recommendation, not a location.
//
// Passing a final test out of order is simply completing that module early.
// Same test, same threshold, no separate storage, no separate reward event.
//
// Everything here is DERIVED from records that already persist:
//   - final test results at assessmentStorageKey(moduleId, 'final')
//     (sticky `everPassed` flag written by Quiz.tsx)
//   - lesson completion via useProgress (webProgressStore)
// No new storage, nothing to migrate, nothing extra to sync.
//
// Pure module, no React. Views re-read on the same 'nis:progress' event
// that lesson writes (and Quiz result writes) already emit.
//
// NAMING NOTE: `positionIndex` / `positionModule` are kept from V1 for API
// stability; read them as "the next-up module". All user-facing copy speaks
// completion, never position.
// ─────────────────────────────────────────────────────────────────────────

import {
	assessmentStorageKey,
	getMapModules,
	type AssessmentId,
	type LearningModule,
} from '@nisk/core/learning';
import { getCompletedLessonIds } from './useProgress';

/** Path-relative state for a Core Module. There is deliberately no 'locked'. */
export type CorePathModuleState =
	| 'completed' // final test passed AND all lessons finished
	| 'tested' // final test passed (module complete); lessons still open
	| 'current' // the next-up module: first whose final isn't passed
	| 'ahead'; // later in the recommended order; open like everything else

export interface CorePathPosition {
	/** Core Path modules in locked product order (map surface only). */
	modules: LearningModule[];
	total: number;
	/** How many modules count as complete (final test passed), in any order. */
	finalsPassed: number;
	/** Index of the next-up module: the first whose final test isn't passed.
	 *  Equals `total` when every final test on the path has been passed. */
	positionIndex: number;
	/** The next-up module, or null when the whole path is complete. */
	positionModule: LearningModule | null;
	/** True when every final test on a non-empty path is passed. */
	complete: boolean;
}

export interface ModulePathInfo {
	index: number;
	/** 1-based display number ("Core Module 4"). */
	number: number;
	total: number;
	state: CorePathModuleState;
	/** 1-based display number of the next-up module (clamped to `total`
	 *  when the path is complete). */
	positionNumber: number;
	positionComplete: boolean;
}

// ── Assessment reads ─────────────────────────────────────────────────────

/** True if the given assessment has ever been passed (sticky `everPassed`
 *  flag, with legacy `passed` fallback). */
export function isAssessmentPassed(
	moduleId: string,
	assessmentId: AssessmentId,
): boolean {
	try {
		const raw = localStorage.getItem(
			assessmentStorageKey(moduleId, assessmentId),
		);
		if (!raw) return false;
		const parsed = JSON.parse(raw) as {
			passed?: boolean;
			everPassed?: boolean;
		};
		return !!(parsed.everPassed || parsed.passed);
	} catch {
		return false;
	}
}

// ── Path + completion ────────────────────────────────────────────────────

/** The Core Path spine, in locked product order (surface 'map' only). */
export function getCorePathModules(): LearningModule[] {
	return getMapModules();
}

/** Index of a module on the Core Path, or -1 (school / off-path modules). */
export function getCorePathIndexOf(moduleId: string): number {
	return getCorePathModules().findIndex(
		(m: LearningModule) => m.id === moduleId,
	);
}

export function getCorePathPosition(): CorePathPosition {
	const modules = getCorePathModules();
	const total = modules.length;
	let positionIndex = total;
	let finalsPassed = 0;
	for (let i = 0; i < total; i++) {
		if (isAssessmentPassed(modules[i].id, 'final')) {
			finalsPassed++;
		} else if (positionIndex === total) {
			positionIndex = i;
		}
	}
	return {
		modules,
		total,
		finalsPassed,
		positionIndex,
		positionModule: positionIndex < total ? modules[positionIndex] : null,
		complete: total > 0 && finalsPassed === total,
	};
}

/** State of the module at `index` relative to the next-up marker. Any
 *  module whose final test is passed counts as complete; the only question
 *  left there is whether the lessons are finished too. */
export function getCorePathModuleState(
	index: number,
	positionIndex: number,
	flags: { finalPassed: boolean; allLessonsDone: boolean },
): CorePathModuleState {
	if (flags.finalPassed) {
		return flags.allLessonsDone ? 'completed' : 'tested';
	}
	if (index === positionIndex) return 'current';
	return 'ahead';
}

/** Full path context for one module (null when not on the Core Path). */
export function getModulePathInfo(moduleId: string): ModulePathInfo | null {
	const position = getCorePathPosition();
	const index = position.modules.findIndex(
		(m: LearningModule) => m.id === moduleId,
	);
	if (index < 0) return null;

	const mod = position.modules[index];
	const doneCount = getCompletedLessonIds(mod.id).length;
	const allLessonsDone =
		mod.lessons.length > 0 && doneCount === mod.lessons.length;
	const finalPassed = isAssessmentPassed(mod.id, 'final');

	return {
		index,
		number: index + 1,
		total: position.total,
		state: getCorePathModuleState(index, position.positionIndex, {
			finalPassed,
			allLessonsDone,
		}),
		positionNumber: Math.min(position.positionIndex + 1, position.total),
		positionComplete: position.complete,
	};
}

// ── Anchor copy ──────────────────────────────────────────────────────────

/** The single-sentence completion anchor ("4 of 12 Core Modules complete."). */
export function corePathAnchorLine(): string {
	const p = getCorePathPosition();
	if (p.total === 0) return '';
	if (p.complete) return `All ${p.total} Core Modules complete.`;
	return `${p.finalsPassed} of ${p.total} Core Modules complete.`;
}
